<!--
Copyright (c) 2020 ForgeRock. All rights reserved.

This software may be modified and distributed under the terms
of the MIT license. See the LICENSE file for details.
-->

<template>
    <div id="recaptchaContainer"></div>
</template>

<script>
/**
 * @description Selfservice stage for multiple selfservice flows, displays a google captcha
 *
 **/

export default {
    name: 'Captcha',
    props: {
        selfServiceDetails: { required: true }
    },
    data () {
        return {
            recaptchaUri: this.selfServiceDetails.requirements.recaptcha.recaptchaUri,
            recaptchaSiteKey: this.selfServiceDetails.requirements.recaptcha.recaptchaSiteKey,
            scriptLoadPromise: null,
            readyPromise: null
        };
    },
    mounted () {
        console.log('Captcha mounted: disabling submit button until grecaptcha is ready');
        this.$emit('captcha-loading');
        this.readyPromise = this.loadRecaptchaScript().then(() => {
            if (!window.grecaptcha || !window.grecaptcha.enterprise) {
                throw new Error('grecaptcha.enterprise is not available after script load');
            }
            return new Promise((resolve) => {
                window.grecaptcha.enterprise.ready(() => {
                    console.log('grecaptcha.enterprise is ready: enabling submit button');
                    this.$emit('captcha-ready');
                    resolve();
                });
            });
        }).catch((err) => {
            console.error('reCAPTCHA initialization failed, re-enabling submit button:', err);
            this.scriptLoadPromise = null;
            this.$emit('captcha-error', err);
            this.$emit('captcha-ready');
        });
    },
    methods: {
        loadRecaptchaScript () {
            if (window.grecaptcha && window.grecaptcha.enterprise) {
                return Promise.resolve();
            }

            if (this.scriptLoadPromise) {
                return this.scriptLoadPromise;
            }

            this.scriptLoadPromise = new Promise((resolve, reject) => {
                const src = `${this.recaptchaUri}?render=${this.recaptchaSiteKey}`;
                const existing = document.querySelector('script[src*="recaptcha"]');

                if (existing) {
                    existing.addEventListener('load', () => resolve(), { once: true });
                    existing.addEventListener('error', () => reject(new Error('recaptcha script failed')), { once: true });
                    return;
                }

                const recaptchaScript = document.createElement('script');
                recaptchaScript.async = true;
                recaptchaScript.setAttribute('src', src);
                recaptchaScript.addEventListener('load', () => resolve(), { once: true });
                recaptchaScript.addEventListener('error', () => reject(new Error('recaptcha script failed')), { once: true });
                document.head.appendChild(recaptchaScript);
            });

            return this.scriptLoadPromise;
        },
        async executeScore (action) {
            await this.readyPromise;
            if (!window.grecaptcha || !window.grecaptcha.enterprise) {
                throw new Error('grecaptcha.enterprise is not available');
            }
            try {
                return await window.grecaptcha.enterprise.execute(this.recaptchaSiteKey, { action });
            } catch (e) {
                console.log(e);
                throw e;
            }
        }
    }
};
</script>
